import type { Dispatch, SetStateAction } from 'react';
import type {
  AppointmentClickEvent,
  AppointmentDblClickEvent,
  AppointmentTooltipShowingEvent,
  AppointmentUpdatingEvent,
} from 'pos-client/components/customScheduler/types/schedulerTypes';
import type {
  AppointmentAddingEvent,
  AppointmentDraggingStartEvent,
  ContextMenuItemClickEvent,
} from 'pos-client/components/scheduleBoard/types/boardTypes.ts';
import type { ScheduleBoardJobModel } from 'pos-shared/models/schedulingSchema.ts';

import { getDuration, useEvent } from './ScheduleBoardUtils.ts';

export const useHandleJobDragStart = (
  setUserIsDragging: Dispatch<SetStateAction<boolean>>
): ((e: AppointmentDraggingStartEvent) => void) => {
  return useEvent((e: AppointmentDraggingStartEvent) => {
    const completedOrCancelled =
      e.itemData.state === 'complete' || e.itemData.state === 'cancelled';

    if (completedOrCancelled) {
      e.cancel = true;
    }

    if (!e.itemData.allowDragAndDropReschedule) {
      e.cancel = true;
    }
    setUserIsDragging(true);
  });
};

export const handleContextMenuJobFormOpening = (
  e: ContextMenuItemClickEvent
) => {
  e.itemData.cancel = true;
};

export const handleJobDoubleClick = (_e: AppointmentDblClickEvent) => {
  // Custom scheduler handles double-click natively
};

export const hideJobTooltip = (
  _e: AppointmentTooltipShowingEvent,
  _modalIsOpen: boolean
) => {
  // Custom scheduler handles tooltip prevention natively
};

export const handleJobClick = (
  e: AppointmentClickEvent,
  handleOpenModal: (e: AppointmentClickEvent) => void
) => {
  const appointmentData = e.appointmentData as unknown as ScheduleBoardJobModel;
  if (!appointmentData?.text?.toLowerCase().includes('blocked')) {
    handleOpenModal(e);
  }
};

export const handleOpenModal = (
  e: AppointmentClickEvent,
  setSelectedOrderDetailsForAssignTechnician: (details: {
    orderId: string;
    jobId: string;
  }) => void,
  setJobDetailsModalData: (data: ScheduleBoardJobModel) => void
) => {
  const appointmentData = e.appointmentData as unknown as ScheduleBoardJobModel;
  if (!appointmentData?.text?.toLowerCase().includes('blocked')) {
    setSelectedOrderDetailsForAssignTechnician({
      orderId: appointmentData?.orderId ?? '',
      jobId: appointmentData?.jobId ?? '',
    });
    setJobDetailsModalData(appointmentData as ScheduleBoardJobModel);
  }
};

export const useHandleJobUpdating = (): ((
  e: AppointmentUpdatingEvent
) => void) => {
  return useEvent((e: AppointmentUpdatingEvent) => {
    const appointmentData =
      e.appointmentData as unknown as ScheduleBoardJobModel;
    const newData = e.newData as unknown as Partial<ScheduleBoardJobModel>;

    const durationOld = getDuration(
      appointmentData.startDate,
      appointmentData.endDate
    );
    const durationNew = getDuration(
      newData.startDate ?? '',
      newData.endDate ?? ''
    );
    const isResize = durationOld !== durationNew;

    if (isResize && !(newData as any).allowResizingReschedule) {
      e.cancel = true;
    }
  });
};

export const useHandleJobAdd = (
  setJobDetailsModalData: (data: ScheduleBoardJobModel) => void
): ((e: AppointmentAddingEvent) => void) => {
  return useEvent((e: AppointmentAddingEvent) => {
    setJobDetailsModalData(e.itemData as ScheduleBoardJobModel);
  });
};
